import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { page_size } from 'api/apiUtils';
import { HIDE_TECHNOLOGY_CARD_SENTINEL } from './ObjectTypes';
import { paginationClasses, skeletonClasses } from './tailwindClassnames';
import { PaginationNext, PaginationPrev } from './ProjectIcons';
import TechnologyIcon from './TechnologyIcon';

// The least a card needs to draw itself. Both `ProviderType` and
// `DestinationType` satisfy it, so the Route wizard and the Connection screen
// render the same grid instead of each keeping a copy.
export type TechnologyCardItem = {
  id: string;
  name: string;
  // The logo lookup key, and the suffix of the card's test id.
  value: string;
  description?: string;
};

type TechnologyCardGridProps = {
  items: TechnologyCardItem[];
  // Skeleton cards instead of the grid — the caller decides what counts as
  // loading (a pending fetch, a pending reload).
  loading: boolean;
  selectedId: string | null;
  onSelect: (item: TechnologyCardItem) => void;
  // Card test ids read `${testIdPrefix}-card-${item.value}`.
  testIdPrefix: string;
  // What to show when there is nothing to list. The screens word this
  // differently (a failed fetch vs. an empty search), so it comes from outside.
  noResults?: React.ReactNode;
};

export const getPageNumbers = (
  currentPage: number,
  totalPages: number,
): (number | '...')[] => {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
  if (currentPage < 4) return [0, 1, 2, 3, 4, '...', totalPages - 1];
  if (currentPage >= totalPages - 4)
    return [
      0,
      '...',
      totalPages - 5,
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
    ];
  return [
    0,
    '...',
    currentPage - 1,
    currentPage,
    currentPage + 1,
    '...',
    totalPages - 1,
  ];
};

const GRID_CLASSES =
  'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4';

const TechnologyCardGrid = ({
  items,
  loading,
  selectedId,
  onSelect,
  testIdPrefix,
  noResults,
}: TechnologyCardGridProps) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);

  const totalPages = Math.ceil(items.length / page_size);
  const pageItems = items.slice(
    currentPage * page_size,
    (currentPage + 1) * page_size,
  );

  // Back to page one when the LIST changes, not when the caller merely hands
  // over a freshly-built array with the same contents. A parent that filters
  // inline (the Connection screen) rebuilds `items` on every render, so an
  // identity dep would snap the user back to page one on any unrelated
  // re-render — an organizations fetch resolving, say.
  const itemsKey = items.map((item) => item.id).join('|');
  useEffect(() => {
    setCurrentPage(0);
  }, [itemsKey]);

  const goToPage = (page: number) => {
    if (page < 0 || page >= totalPages) return;
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <div className={GRID_CLASSES}>
        {Array.from({ length: page_size }).map((_, i) => (
          <div
            key={i}
            className="flex h-[190px] w-full flex-col items-center gap-2 overflow-hidden rounded-2xl border-[3px] border-teal-t30 bg-white p-4"
          >
            <div
              className={`h-[31px] w-[31px] flex-shrink-0 rounded-full ${skeletonClasses.base}`}
            />
            <div className={`h-4 w-20 ${skeletonClasses.base}`} />
            <div className={`h-3 w-24 ${skeletonClasses.base}`} />
            <div className={`h-3 w-20 ${skeletonClasses.base}`} />
            <div className={`h-3 w-16 ${skeletonClasses.base}`} />
          </div>
        ))}
      </div>
    );
  }

  if (items.length < 1)
    return (
      <div className="rounded-lg border border-teal-t30 bg-white py-8">
        {noResults}
      </div>
    );

  return (
    <>
      <div className={GRID_CLASSES}>
        {pageItems.map((item: TechnologyCardItem) => (
          <div
            key={item.id || item.value}
            role="button"
            tabIndex={0}
            aria-label={item.name}
            data-testid={`${testIdPrefix}-card-${item.value}`}
            onClick={() => onSelect(item)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(item);
              }
            }}
            className={`flex h-[190px] w-full cursor-pointer flex-col items-center gap-2 overflow-hidden rounded-2xl border-[3px] bg-white p-4 transition-colors duration-300 ${
              selectedId === item.id && item.id !== ''
                ? 'border-brand-accent-green'
                : 'border-teal-t30 hover:border-brand-accent-green'
            }`}
          >
            {/* Circle is 31px; logo is 38px — intentional 1.2x zoom-crop via overflow:hidden */}
            <TechnologyIcon
              typeValue={item.value}
              typeName={item.name}
              boxClassName="w-[31px] h-[31px] text-base"
              imgClassName="w-[38px] h-[38px] object-cover"
            />
            <p className="line-clamp-2 text-center font-medium text-ui-text-primary-text">
              {item.name}
            </p>
            {/* The hide-card sentinel lives in `description`. It is a
                            marker, never copy — a surface that doesn't filter on
                            it would otherwise print it under the logo. */}
            {item.description &&
              item.description !== HIDE_TECHNOLOGY_CARD_SENTINEL && (
                <p className="line-clamp-3 text-center text-sm text-ui-text-primary-text">
                  {item.description}
                </p>
              )}
          </div>
        ))}
      </div>
      {totalPages > 1 && (
        /* The same controls the list pages use (paginationClasses): white
                   boxes with a light border, the active page filled teal, and the
                   shared chevrons rather than arrow glyphs. This grid pages itself
                   rather than going through <Pagination/> — it has no row count to
                   report and sits centred under the cards — so the chrome is
                   borrowed instead of the component. */
        <div className="mt-4 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 0}
            aria-label={t('Previous page')}
            className={`${paginationClasses.button} disabled:opacity-30 disabled:hover:bg-white`}
          >
            <PaginationPrev />
          </button>
          {getPageNumbers(currentPage, totalPages).map((page, idx) =>
            page === '...' ? (
              <span
                key={`ellipsis-${idx}`}
                aria-hidden="true"
                className={`px-2 py-1 ${paginationClasses.text}`}
              >
                …
              </span>
            ) : (
              <button
                type="button"
                key={page}
                onClick={() => goToPage(page as number)}
                aria-current={currentPage === page ? 'page' : undefined}
                aria-label={`${t('Page')} ${(page as number) + 1}`}
                className={`${paginationClasses.button} ${
                  currentPage === page ? paginationClasses.buttonActive : ''
                }`}
              >
                {(page as number) + 1}
              </button>
            ),
          )}
          <button
            type="button"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= totalPages - 1}
            aria-label={t('Next page')}
            className={`${paginationClasses.button} disabled:opacity-30 disabled:hover:bg-white`}
          >
            <PaginationNext />
          </button>
        </div>
      )}
    </>
  );
};

export default TechnologyCardGrid;
